import React, { useEffect, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { supabase } from 'components/supabase/supabaseClient';

// ✅ Utility to format timestamps like "5m ago"
const formatTimeAgo = (timestamp) => {
  const date = new Date(timestamp);
  const now = new Date();
  const seconds = Math.floor((now - date) / 1000);

  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  const weeks = Math.floor(days / 7);
  if (weeks < 4) return `${weeks}w ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  const years = Math.floor(days / 365);
  return `${years}y ago`;
};

const AddResponseModal = ({ ticket, isOpen, onClose, onUpdate }) => {

    const [isEditing, setIsEditing] = useState(false);
    const [editedTicket, setEditedTicket] = useState(ticket || {});
    const [newResponse, setNewResponse] = useState('');
    const [isInternalNote, setIsInternalNote] = useState(false);
    const [responses, setResponses] = useState([]);

    // 🔽 Fetch replies when modal opens
    useEffect(() => {
    const fetchReplies = async () => {
        if (!ticket?.id) return;

        // 1. Fetch replies
        const { data: replies, error: repliesError } = await supabase
        .from("ticket_replies")
        .select("*")
        .eq("ticket_id", ticket.id)
        .order("created_at", { ascending: true });

        if (repliesError) {
        console.error("Error fetching replies:", repliesError.message);
        return;
        }

        if (!replies || replies.length === 0) {
        setResponses([]);
        return;
        }

        const userIds = [...new Set(replies.map((r) => r.user_id))];

        const { data: profiles, error: profilesError } = await supabase
        .from("profiles")
        .select("id, user_id, name")
        .in("user_id", userIds);

        if (profilesError) {
        console.error("Error fetching profiles:", profilesError.message);
        }

        const responsesWithNames = replies.map((reply) => {
        const profile = profiles?.find((p) => p.user_id === reply.user_id);
        return {
            ...reply,
            user_name: profile?.name || reply.user_id, 
        };
        });

        setResponses(responsesWithNames);
    };

    if (isOpen) {
        fetchReplies();
    }
    }, [ticket, isOpen]);

    console.log('Responses:', responses);


    const handleAddResponse = async () => {
    if (!newResponse.trim() || !ticket?.id) return;

    const response = {
        ticket_id: ticket.id,
        user_id: '4725e89b-74b7-41d9-93d5-b6e59111cca7',
        content: newResponse,
    };

    const { data, error } = await supabase
        .from('ticket_replies')
        .insert([response])
        .select();

    if (error) {
        console.error('Error adding reply:', error.message);
    } else {
        setResponses((prev) => [...prev, ...data]);
        setNewResponse('');
        setIsInternalNote(false);
    }
    };


if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center  p-4" style={{zIndex:'1003'}}>
      <div className="bg-card border border-border rounded-lg shadow-elevation-3 w-full max-w-4xl  overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-accent/10 rounded-lg flex  justify-center">
              <Icon name="MessageSquare" size={20} className="text-accent" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-text-primary">
                Responses & Updates
              </h2>
              <p className="text-sm text-text-secondary">
                Communication history and internal notes
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <p className="text-sm text-text-secondary">
              {ticket?.id?.toString().slice(0, 8) || '---'}
            </p>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <Icon name="X" size={20} />
            </Button>
          </div>
        </div>

        <div className="flex ">
          {/* Right Panel - Responses */}
          <div className="w-full flex flex-col">

            {/* Responses List */}
            <div className="flex-1 p-6 overflow-y-auto space-y-4 max-h-[60vh]">
                {responses.map((response) => (
                <div key={response.id} className="border border-border rounded-lg p-4">
                    <div className="flex items-start space-x-3">
                    <div className="w-8 h-8 rounded-full bg-accent/10 flex items-center justify-center text-xs font-bold">
                        {response.user_id?.slice(0, 2).toUpperCase()}
                    </div>
                    <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-text-primary">
                            {response.user_name}
                        </span>
                        <span className="text-xs text-text-secondary">
                            {formatTimeAgo(response.created_at)}
                        </span>
                        </div>
                        <p
                        className="text-sm text-text-primary"
                        dangerouslySetInnerHTML={{ __html: response.content }}
                        />
                    </div>
                    </div>
                </div>
                ))}
            </div>

            {/* New Response Form */}
            <div className="p-6 border-t border-border">
              <div className="space-y-3">
                <label className="block text-sm font-medium text-text-secondary">
                  Add Response
                </label>
                <textarea
                  value={newResponse}
                  onChange={(e) => setNewResponse(e.target.value)}
                  placeholder="Type your response here..."
                  className="w-full h-24 px-3 py-2 bg-input border border-border rounded-md text-sm text-text-primary placeholder-text-secondary resize-none focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="internal-note"
                      checked={isInternalNote}
                      onChange={(e) => setIsInternalNote(e.target.checked)}
                      className="rounded border-border text-primary focus:ring-primary"
                    />
                    <label
                      htmlFor="internal-note"
                      className="text-sm text-text-secondary"
                    >
                      Internal note (not visible to customer)
                    </label>
                  </div>
                  <Button
                    variant="default"
                    size="sm"
                    onClick={handleAddResponse}
                    disabled={!newResponse.trim()}
                    iconName="Send"
                  >
                    Send Response
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddResponseModal;
